// src/hooks/useKeyboardInset.js
//
// How much of the screen the software keyboard is currently covering, in CSS
// pixels, and whether it is open at all.
//
// Why this exists
// ---------------
// On iOS the software keyboard does NOT shrink the layout viewport. WKWebView
// keeps `window.innerHeight`, `100vh`, `100dvh` and `100svh` at their full
// values and slides the keyboard over the top of the page, then asks the native
// scroll view to scroll the focused field into view. That last step only works
// when the DOCUMENT is the scroller. Every auth and funnel screen in this app
// is deliberately built the other way round: a `fixed inset-0` (or `h-[100svh]`)
// shell whose body is a nested `overflow-y-auto` panel, so the CTA can be pinned
// outside the scroll area. In that shape iOS has nothing to scroll, and a
// focused input plus the sticky Continue button below it end up underneath the
// keyboard with no way to reach either.
//
// That is the account-creation step of a paid funnel and the post-payment code
// entry screen, so it is a revenue bug, not a polish item.
//
// The fix is to measure the keyboard with the Visual Viewport API (which DOES
// report the covered area on iOS) and let the shell shrink to the visible
// region. `bottom: <inset>px` on a fixed shell, or an explicit pixel height on
// a `100svh` shell, is enough: the existing internal scroll area then does
// exactly what it already does on a short phone.
//
// Notes
// -----
//   - Zero behaviour change where `visualViewport` is missing: the inset is
//     always 0 and every caller renders exactly as it does today.
//   - `MIN_KEYBOARD_PX` ignores the small viewport changes Safari makes when it
//     collapses its own URL bar, so a scroll gesture is never mistaken for a
//     keyboard.
//   - The value is only ever read during layout, never animated, so there is
//     nothing here for `prefers-reduced-motion` to suppress.

import { useEffect, useState } from 'react';

/**
 * Anything smaller than this is browser chrome moving, not a keyboard. The
 * shortest software keyboard on any device in the iPhone/iPad matrix is well
 * over 200pt; Safari's collapsing URL bar is around 50pt.
 */
const MIN_KEYBOARD_PX = 120;

function measure() {
  if (typeof window === 'undefined') return 0;
  const vv = window.visualViewport;
  if (!vv) return 0;
  // The part of the layout viewport that the visual viewport no longer covers.
  // offsetTop is included because iOS may also shift the visual viewport up
  // when it scrolls a focused field into view.
  const covered = window.innerHeight - vv.height - vv.offsetTop;
  if (!Number.isFinite(covered) || covered < MIN_KEYBOARD_PX) return 0;
  return Math.round(covered);
}

/**
 * Live height, in CSS pixels, of the region the software keyboard is covering
 * at the bottom of the layout viewport. 0 when no keyboard is up.
 *
 * @returns {number}
 */
export function useKeyboardInset() {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;

    let frame = 0;
    const sync = () => {
      cancelAnimationFrame(frame);
      // One read per frame: iOS fires resize and scroll together, many times,
      // across the keyboard animation.
      frame = requestAnimationFrame(() => setInset(measure()));
    };

    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    window.addEventListener('orientationchange', sync);
    return () => {
      cancelAnimationFrame(frame);
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      window.removeEventListener('orientationchange', sync);
    };
  }, []);

  return inset;
}

/**
 * Bring the focused field back into view after the shell has resized around a
 * newly opened keyboard.
 *
 * Shrinking the shell is only half the fix. iOS scrolls a focused field into
 * view by scrolling the native scroll view, which in these layouts holds
 * nothing: the real scroller is a nested `overflow-y-auto` div. So once the
 * shell has shrunk, the field the user just tapped can be below the fold of
 * that inner panel with nothing to put it back. `scrollIntoView` walks every
 * scrollable ancestor, so one call fixes it wherever the panel happens to be.
 *
 * Pass the value from useKeyboardInset(). Does nothing when it is 0, and
 * nothing when the focused element is not a text field.
 *
 * @param {number} inset
 */
export function useScrollFocusedIntoView(inset) {
  useEffect(() => {
    if (!inset || typeof document === 'undefined') return undefined;
    const el = document.activeElement;
    if (!el || !/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName || '')) return undefined;
    // One frame, so the shell has already been laid out at its new height.
    const id = requestAnimationFrame(() => {
      let behavior = 'smooth';
      try {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) behavior = 'auto';
      } catch {
        /* default to smooth */
      }
      try {
        el.scrollIntoView({ block: 'center', behavior });
      } catch {
        el.scrollIntoView();
      }
    });
    return () => cancelAnimationFrame(id);
  }, [inset]);
}

/**
 * Inline style for a `fixed inset-0` shell so it ends where the keyboard
 * begins. Spread it onto the shell's existing style object, AFTER the
 * `paddingBottom` safe-area line: while the keyboard is up the home indicator
 * is behind it, so reserving space for it would push the CTA back under the
 * keys it was just rescued from.
 *
 * @param {number} inset value from useKeyboardInset()
 * @returns {{bottom: string, paddingBottom?: string}}
 */
export function keyboardFixedShellStyle(inset) {
  if (!inset) return {};
  return { bottom: `${inset}px`, paddingBottom: '0px' };
}

/**
 * Inline style for a shell sized with `h-[100svh]` / `min-h-[100dvh]`. Returns
 * an explicit pixel height equal to the visible region, which wins over the
 * class, and nothing at all when no keyboard is up.
 *
 * @param {number} inset value from useKeyboardInset()
 * @returns {{height?: string}}
 */
export function keyboardShellHeightStyle(inset) {
  if (!inset || typeof window === 'undefined') return {};
  const vv = window.visualViewport;
  const height = vv ? Math.round(vv.height) : window.innerHeight - inset;
  return { height: `${height}px` };
}
